import React, { useMemo } from 'react';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { TreeList } from '@scale/scaleui';
import { ISensorFusionUnifiedTaskLookupDto } from '@scale/sensor-fusion-shared';

import { LookupIdLink } from '../common';
import { CORP_LOOKUP_PATH, LIDARLITE_DEBUG_TASK_PATH, REMOADMIN_LOOKUP_PATH } from '../constants';
import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';
import { SubstageInstanceListItem } from 'react-scale/dashboard.scale.com/components/SensorFusionLookup/common/SubstageInstanceListItem';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

type SfuTaskOverviewProps = {
  task: ISensorFusionUnifiedTaskLookupDto;
};

const SfuTaskOverview = ({ task }: SfuTaskOverviewProps) => {
  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};
  const { isRemoAdmin } = useRemoAdminCtx();

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  const { project } = task;
  const { stageInstances } = task;

  const debugUrl = `${toolUrl}/${LIDARLITE_DEBUG_TASK_PATH}${task._id}`;
  const projectUrl = `${toolUrl}/${isRemoAdmin ? REMOADMIN_LOOKUP_PATH : CORP_LOOKUP_PATH}/${
    project.id
  }`;

  return (
    <div className="border-solid border border-neutral-200 px-4 py-4 my-4 w-full overflow-wrap-anywhere">
      <div className="flex flex-col gap-2 py-1">
        <h4 className="font-bold text-lg">Overview</h4>
        <div>
          <span className="font-bold">Project: </span>
          {`${project.name} `}
          <WithCopy className="inline-block text-info-700" copyText={project.id}>
            <a href={projectUrl}>{project.id}</a>
          </WithCopy>
        </div>
        <div>
          <span className="font-bold">Status: </span>
          {task.status}
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col gap-1 w-3/4">
            <span className="font-medium">Stage Instances: </span>
            {stageInstances ? (
              <TreeList childContainerClassName="max-w-full">
                {stageInstances?.map((stageInstance, stageIndex) => (
                  <div className="flex flex-col" key={`stage_list_item_${stageIndex}`}>
                    <div>
                      <div>
                        <span className="font-bold">Stage </span>
                        {stageInstance.defName}
                        <LookupIdLink className="mx-1 inline-block" id={stageInstance.id} />
                        <span>({stageInstance.status})</span>
                      </div>
                    </div>

                    {stageInstance.substageInstances.length > 0 && (
                      <TreeList>
                        {stageInstance.substageInstances.map((substageInstance, index) => (
                          <SubstageInstanceListItem
                            substageInstance={substageInstance}
                            key={`substage_list_item_${index}`}
                          />
                        ))}
                      </TreeList>
                    )}
                  </div>
                ))}
              </TreeList>
            ) : (
              <span className="ml-2">None</span>
            )}
          </div>
          <div className="flex flex-col gap-1 w-1/4">
            <div className="flex flex-col gap-2 py-1">
              <a className="text-info-700" href={debugUrl}>
                Open in debug
              </a>
            </div>
            {task.params.sceneId || !task.params.sceneUrl ? (
              <div>
                <span className="font-medium">Scene ID: </span>
                {task.params.sceneId ? (
                  <LookupIdLink className="inline-block" id={task.params.sceneId} />
                ) : (
                  'None'
                )}
              </div>
            ) : (
              <WithCopy className="inline-block" copyText={task.params.sceneUrl}>
                <a className="text-info-700" href={task.params.sceneUrl}>
                  Scene URL
                </a>
              </WithCopy>
            )}
            <div>
              <span className="font-bold">Created At: </span>
              {new Date(task.createdAt).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .overflow-wrap-anywhere {
          overflow-wrap: anywhere;
        }
      `}</style>
    </div>
  );
};

export default SfuTaskOverview;
